// React
import { useMemo } from 'react';

// Antd
import { Form, Row, Col, Grid, Button, notification } from 'antd';

// Components
import {
  BackOfficeBasePhotoUploader,
  FormLabel,
  FormTextField,
  FormTextArea,
  FormDatePicker,
  FormSelectOption
} from 'components';

// Interfaces
import { ISharedProfileDataProps } from './interfaces';

// Constant
import { FILE_SIZE_0_5_MB } from 'constant/file.constant';
import { GENDER_DROPDOWN } from 'constant/shared.constant';
import { NATION_DROPDOWN } from 'constant/nation.constant';

// Custom Hook
import {
  useValidation,
  useUser,
  useAuth
} from 'hooks';

// i18n
import { useTranslation } from 'react-i18next';

// Moment
import moment from 'moment';

// API
import { userApi } from 'api/webpage/user.api';

// Styles
import styles from './index.module.scss';

const SharedProfileData = ({
  profileData,
  form,
  isPersonalDataEditable,
  onFinish,
  onChangeForm,
  handleDialogVerification,
  renderFooter,
  attrs,
  className
}: ISharedProfileDataProps) => {

  const { t } = useTranslation();
  const { validation_validate, validation_required, validation_validEmail } = useValidation();
  const { md, lg, xl } = Grid.useBreakpoint();
  const { user_loading } = useUser();
  const { auth_authenticatedUserRoles } = useAuth();

  const isAdmin = useMemo(() => {
    return auth_authenticatedUserRoles.find(role => role.status)?.name === "SUPER_ADMIN";
  }, [auth_authenticatedUserRoles]);

  const personalDataFormStyle = useMemo(() => {
    return {
      display: isPersonalDataEditable ? '' : 'none',
      width: md || lg || xl ? '295px' : '100%',
    };
  }, [isPersonalDataEditable, md, lg, xl]);

  const handleDownloadIdCard = async () => {
    try {
      if (profileData.idCardPhoto) {
        const urlParts = profileData.idCardPhoto.split('/');
        const blobId = urlParts[urlParts.length - 2];

        if (blobId) {
          // Get the auth token from localStorage
          const token = 'Bearer ' + localStorage.getItem('lspAccessToken');

          // Get user name for the file name
          const userName = profileData.name || 'User';
          // Clean the name to make it file-safe (remove special characters)
          const safeName = userName.replace(/[^a-zA-Z0-9]/g, '_');

          // Call the API with the auth token
          const response = await userApi.downloadZip(blobId, {
            headers: {
              Authorization: token
            }
          });

          // Create a blob with the correct MIME type (application/zip)
          const blob = new Blob([response], { type: 'application/zip' });

          // Generate a filename with user's name and current date
          const date = new Date();
          const formattedDate = date.getFullYear() +
            ('0' + (date.getMonth() + 1)).slice(-2) +
            ('0' + date.getDate()).slice(-2) +
            ('0' + date.getHours()).slice(-2) +
            ('0' + date.getMinutes()).slice(-2) +
            ('0' + date.getSeconds()).slice(-2);

          // Create download link with user name in the filename
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `KTP_${safeName}_${formattedDate}.zip`;

          // Trigger download
          link.click();

          // Clean up
          window.URL.revokeObjectURL(url);

          // Show success notification
          notification.success({
            message: t('profileData.notification.downloadSuccessTitle'),
            description: t('profileData.notification.downloadSuccess') + ' (The file is password-protected)',
            placement: 'topRight',
            duration: 6
          });
        }
      }
    } catch (error) {
      console.error('Error downloading ID card:', error);
      notification.error({
        message: t('profileData.notification.downloadErrorTitle'),
        description: t('profileData.notification.downloadError'),
        placement: 'topRight',
        duration: 4.5
      });
    }
  };

  const handleDownloadPasPhoto = async () => {
    try {
      if (profileData.pasPhoto) {
        const urlParts = profileData.pasPhoto.split('/');
        const blobId = urlParts[urlParts.length - 2];

        if (blobId) {
          // Get the auth token from localStorage
          const token = 'Bearer ' + localStorage.getItem('lspAccessToken');

          // Get user name for the file name
          const userName = profileData.name || 'User';
          // Clean the name to make it file-safe (remove special characters)
          const safeName = userName.replace(/[^a-zA-Z0-9]/g, '_');

          // Call the API with the auth token
          const response = await userApi.downloadZip(blobId, {
            headers: {
              Authorization: token
            }
          });

          // Create a blob with the correct MIME type (application/zip)
          const blob = new Blob([response], { type: 'application/zip' });

          // Generate a filename with user's name and current date
          const date = new Date();
          const formattedDate = date.getFullYear() +
            ('0' + (date.getMonth() + 1)).slice(-2) +
            ('0' + date.getDate()).slice(-2) +
            ('0' + date.getHours()).slice(-2) +
            ('0' + date.getMinutes()).slice(-2) +
            ('0' + date.getSeconds()).slice(-2);

          // Create download link with user name in the filename
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `PasPhoto_${safeName}_${formattedDate}.zip`;

          // Trigger download
          link.click();

          // Clean up
          window.URL.revokeObjectURL(url);

          // Show success notification
          notification.success({
            message: t('profileData.notification.downloadPasPhotoSuccessTitle'),
            description: t('profileData.notification.downloadPasPhotoSuccess') + ' (The file is password-protected)',
            placement: 'topRight',
            duration: 6
          });
        }
      }
    } catch (error) {
      console.error('Error downloading Pas Photo:', error);
      notification.error({
        message: t('profileData.notification.downloadPasPhotoErrorTitle'),
        description: t('profileData.notification.downloadPasPhotoError'),
        placement: 'topRight',
        duration: 4.5
      });
    }
  };

  return <>
    {/* Form */}
    <Form name="profile-form" form={form} onFinish={onFinish} scrollToFirstError autoComplete="off" requiredMark={false} className={`${className ? className : ''}`}>
      <Row gutter={20} style={{ marginLeft: attrs?.notIncludePhotoProfile ? 10 : -10 }}>
        {/*  Profile Photo */}
        {
          !attrs?.notIncludePhotoProfile && <Col xs={24} sm={24} md={9} lg={9} xl={9}>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                return (
                  <Form.Item name="profilePhoto">
                    <BackOfficeBasePhotoUploader
                      image={getFieldValue('profilePhoto') || profileData.profilePhoto || ''}
                      onChange={file => onChangeForm && onChangeForm('profilePhoto', file)}
                      maximumFileSize={FILE_SIZE_0_5_MB}
                      isEditable={isPersonalDataEditable}
                      withoutCard={attrs?.photoProfileWithoutCard}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        }

        {/* Personal Data */}
        <Col xs={24} sm={24} md={15} lg={15} xl={15}>
          {/* Name */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.name" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="name"
                rules={validation_validate(['required', 'alphaNumeric'], 'profileData.form.name')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="profileData.formPlaceholder.name" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.name || '-'} />}
            </Col>
          </Row>

          {/* Email */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.email" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="email"
                rules={[validation_required('profileData.form.email'), validation_validEmail()]}
                style={personalDataFormStyle}
              >
                <FormTextField
                  placeholder="profileData.formPlaceholder.email"
                  className="personal-data"
                  isEmail
                  loading={user_loading.isActionLoading}
                  readOnly
                />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.email || '-'} />}
            </Col>
          </Row>

          {/* Phone Number */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.phone" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <div className="flex items-center">
                <Form.Item
                  name="phoneNumber"
                  rules={validation_validate(['required', 'numeric'], 'profileData.form.phone')}
                  style={personalDataFormStyle}
                >
                  <FormTextField placeholder="profileData.formPlaceholder.phone" className="personal-data" isPhoneNumber />
                </Form.Item>
                {!isPersonalDataEditable && <FormLabel label={profileData.phoneNumber || '-'} />}
                {profileData.phoneNumber && !profileData.isPhoneNumberValidated && attrs?.needPhoneNumberVerification && (
                  <p
                    className="font-13 text-primary font-weight-700 cursor-pointer ml-4"
                    onClick={() => handleDialogVerification && handleDialogVerification(true)}
                  >
                    {t('app.verification')}
                  </p>
                )}
              </div>
              {!isPersonalDataEditable && attrs?.needPhoneNumberVerification && (
                <div className="flex items-center">
                  <FormLabel label={profileData.phoneNumber || '-'} isPhoneNumber />
                  {profileData.phoneNumber && !profileData.isPhoneNumberValidated && (
                    <p
                      className="font-13 text-primary font-weight-700 cursor-pointer ml-4"
                      onClick={() => handleDialogVerification && handleDialogVerification(true)}
                    >
                      {t('app.verification')}
                    </p>
                  )}
                </div>
              )}
            </Col>
          </Row>

          {/* Address */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.address" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="address"
                rules={validation_validate(['required'], 'profileData.form.address')}
                style={personalDataFormStyle}
              >
                <FormTextArea placeholder="profileData.formPlaceholder.address" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.address || '-'} />}
            </Col>
          </Row>

          {/* Gender */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.gender" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="gender"
                rules={validation_validate(['required'], 'app.user.form.gender')}
                style={personalDataFormStyle}
              >
                <FormSelectOption placeholder="app.user.formPlaceholder.gender" list={GENDER_DROPDOWN} />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.gender || '-'} />}
            </Col>
          </Row>

          {/* Birth Date */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.birthDate" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="birthDate"
                rules={validation_validate(['required'], 'profileData.form.birthDate')}
                style={personalDataFormStyle}
              >
                <FormDatePicker placeholder="profileData.formPlaceholder.birthDate" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && (
                <FormLabel
                  label={
                    moment(profileData.birthDate).format('DD MMMM YYYY').toString() === 'Invalid date'
                      ? '-'
                      : moment(profileData.birthDate).format('DD MMMM YYYY').toString()
                  }
                />
              )}
            </Col>
          </Row>

          {/* Birth Place */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.birthPlace" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="birthPlace"
                rules={validation_validate(['required'], 'app.user.form.birthPlace')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.birthPlace" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.birthPlace || '-'} />}
            </Col>
          </Row>

          {/* NPK */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.npk" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="npk"
                rules={validation_validate(['required', 'numeric'], 'profileData.form.npk')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="profileData.formPlaceholder.npk" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.npk || '-'} />}
            </Col>
          </Row>

          {/* NIK */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.nik" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="nik"
                rules={validation_validate(['required', 'numeric'], 'profileData.form.nik')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="profileData.formPlaceholder.nik" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.nik || '-'} />}
            </Col>
          </Row>

          {/* Nationality */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.nationality" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="nationality"
                rules={validation_validate(['required'], 'app.user.form.nationality')}
                style={personalDataFormStyle}
              >
                <FormSelectOption placeholder="app.user.formPlaceholder.nationality" list={NATION_DROPDOWN} />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.nationality || '-'} />}
            </Col>
          </Row>

          {/* Postal Code */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.postalCode" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="postalCode"
                rules={validation_validate(['required', 'numeric'], 'app.user.form.postalCode')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.postalCode" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.postalCode || '-'} />}
            </Col>
          </Row>

          {/* Phone */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.homePhone" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="phone"
                rules={validation_validate(['required', 'phoneNumber'], 'app.user.form.homePhone')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.homePhone" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.phone || '-'} />}
            </Col>
          </Row>

          {/* Work Phone */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.workPhone" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="workPhone"
                rules={validation_validate(['phoneNumber'], 'app.user.form.workPhone')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.workPhone" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.workPhone || '-'} />}
            </Col>
          </Row>

          {/* Last Education */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.lastEducation" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="lastEducation"
                rules={validation_validate(['required'], 'app.user.form.lastEducation')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.lastEducation" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.lastEducation || '-'} />}
            </Col>
          </Row>

          {/* Institution Name */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.form.institutionName" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="institutionName"
                rules={validation_validate(['required'], 'app.user.form.institutionName')}
                style={personalDataFormStyle}
              >
                <FormTextField placeholder="app.user.formPlaceholder.institutionName" className="personal-data" />
              </Form.Item>
              {!isPersonalDataEditable && <FormLabel label={profileData.institutionName || '-'} />}
            </Col>
          </Row>

          {/* ID Card Photo */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.idCardPhoto" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue, getFieldError }) => {
                  return (
                    <Form.Item
                      noStyle
                      name="idCardPhoto"
                      rules={validation_validate([''], 'profileData.form.idCardPhoto')}
                    >
                      <div className="flex flex-col gap-2">
                        <div className={`border rounded-lg p-4 bg-white ${styles['id-card-container']}`}>
                          <div className={`w-full h-48 overflow-hidden rounded-md ${styles['id-card-image']}`}>
                            <BackOfficeBasePhotoUploader
                              image={getFieldValue('idCardPhoto') || profileData.idCardPhoto || ''}
                              onChange={file => onChangeForm && onChangeForm('idCardPhoto', file)}
                              isEditable={isPersonalDataEditable}
                              withoutCard={true}
                              error={getFieldError('idCardPhoto')?.toString()}
                              maximumFileSize={FILE_SIZE_0_5_MB}
                            />
                          </div>
                          {/* Button Download ID Card */}
                          {profileData.idCardPhoto && isAdmin && (
                            <div className="flex justify-center mt-2">
                              {console.log('🔵 ID Card download button is being rendered for Super Admin')}
                              <Button
                                type="primary"
                                onClick={handleDownloadIdCard}
                                style={{
                                  backgroundColor: '#0049B0',
                                  borderColor: '#0049B0',
                                  borderRadius: '4px',
                                  padding: '4px 16px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  height: '32px'
                                }}
                              >
                                {t('profileData.form.downloadIdCard')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          {/* Pas Photo 3x4 */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.form.pasPhoto" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue, getFieldError }) => {
                  return (
                    <Form.Item
                      noStyle
                      name="pasPhoto"
                      rules={validation_validate([''], 'profileData.form.pasPhoto')}
                    >
                      <div className="flex flex-col gap-2">
                        <div className={`border rounded-lg p-4 bg-white ${styles['id-card-container']}`}>
                          <div className={`w-full h-48 overflow-hidden rounded-md ${styles['id-card-image']}`}>
                            <BackOfficeBasePhotoUploader
                              image={getFieldValue('pasPhoto') || profileData.pasPhoto || ''}
                              onChange={file => onChangeForm && onChangeForm('pasPhoto', file)}
                              isEditable={isPersonalDataEditable}
                              withoutCard={true}
                              error={getFieldError('pasPhoto')?.toString()}
                              maximumFileSize={FILE_SIZE_0_5_MB}
                            />
                          </div>
                          {/* Button Download Pas Photo */}
                          {profileData.pasPhoto && isAdmin && (
                            <div className="flex justify-center mt-2">
                              {console.log('🟢 Pas Photo download button is being rendered for Super Admin')}
                              <Button
                                type="primary"
                                onClick={handleDownloadPasPhoto}
                                style={{
                                  backgroundColor: '#0049B0',
                                  borderColor: '#0049B0',
                                  borderRadius: '4px',
                                  padding: '4px 16px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  height: '32px'
                                }}
                              >
                                {t('profileData.form.downloadPasPhoto')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
      {renderFooter && renderFooter()}
    </Form>
  </>
}

// const SharedProfileData = () => {
//   return <></>
// }

export { SharedProfileData }